import { useEffect, useRef, useState } from 'react'
import type { Scene } from '../content'
import { chapterSeconds, formatDuration } from '../content'
import { navigate } from '../router/useRoute'

/**
 * Sticky chapter navigation.
 *
 * Long chapters — Solution runs to nine scenes — are hard to move around in from a
 * horizontal chip row. This rail keeps every scene in the chapter reachable, and tracks which
 * one is in view so the reader always knows where they are.
 */
export function SceneRail({ scenes, chapterTitle, chapterId }: { scenes: Scene[]; chapterTitle: string; chapterId: string }) {
  const [activeId, setActiveId] = useState<string>(scenes[0]?.id ?? '')
  const listRef = useRef<HTMLOListElement>(null)

  useEffect(() => {
    /*
     * Deterministic scrollspy: the active scene is the last one whose top has passed the
     * header line. Measured live on each frame rather than cached — an IntersectionObserver
     * entry carries a geometry snapshot from when it fired, which goes stale after a
     * programmatic scroll and makes the rail lag a scene behind.
     */
    const HEADER = 96
    let frame = 0

    const measure = () => {
      frame = 0
      let current = scenes[0]?.id ?? ''
      for (const scene of scenes) {
        const el = document.getElementById(scene.id)
        if (!el) continue
        if (el.getBoundingClientRect().top <= HEADER) current = scene.id
        else break
      }
      /* At the very bottom the last scene may never reach the line; select it anyway. */
      const atBottom = window.innerHeight + window.scrollY >= document.body.scrollHeight - 2
      if (atBottom) current = scenes[scenes.length - 1]?.id ?? current
      setActiveId((prev) => (prev === current ? prev : current))
    }

    const onScroll = () => {
      if (frame) return
      frame = requestAnimationFrame(measure)
    }

    measure()
    window.addEventListener('scroll', onScroll, { passive: true })
    window.addEventListener('resize', onScroll)
    return () => {
      if (frame) cancelAnimationFrame(frame)
      window.removeEventListener('scroll', onScroll)
      window.removeEventListener('resize', onScroll)
    }
  }, [scenes])

  /* Keep the active item in view when the rail itself is scrollable. */
  useEffect(() => {
    const el = listRef.current?.querySelector<HTMLElement>('[aria-current="true"]')
    el?.scrollIntoView({ block: 'nearest' })
  }, [activeId])

  function go(e: React.MouseEvent, scene: Scene) {
    e.preventDefault()
    const target = document.getElementById(scene.id)
    if (target) {
      target.scrollIntoView({ behavior: 'smooth', block: 'start' })
      /* Keep the URL deep-linkable without a re-render that would jump to the top. */
      window.history.replaceState(null, '', scene.route)
    } else {
      navigate(scene.route)
    }
  }

  const activeIndex = scenes.findIndex((s) => s.id === activeId)

  return (
    <nav className="rail-nav" aria-label={`${chapterTitle} sections`}>
      <div className="rail-nav__inner">
        <p className="rail-nav__title">
          <span className="rail-nav__chapter">{chapterTitle}</span>
          <span className="rail-nav__meta">
            {scenes.length} scenes · {formatDuration(chapterSeconds(chapterId))}
          </span>
        </p>

        <ol className="rail-nav__list" ref={listRef}>
          {scenes.map((scene, i) => {
            const isActive = scene.id === activeId
            return (
              <li key={scene.id}>
                <a
                  href={scene.route}
                  className={`rail-nav__item${isActive ? ' is-active' : ''}${i < activeIndex ? ' is-past' : ''}`}
                  aria-current={isActive ? 'true' : undefined}
                  onClick={(e) => go(e, scene)}
                >
                  <span className="rail-nav__tick" aria-hidden="true" />
                  <span className="rail-nav__id mono">{scene.id}</span>
                  <span className="rail-nav__label">{scene.title}</span>
                </a>
              </li>
            )
          })}
        </ol>

        <p className="rail-nav__progress" aria-hidden="true">
          <span
            className="rail-nav__progress-fill"
            style={{ width: `${((activeIndex + 1) / scenes.length) * 100}%` }}
          />
        </p>
      </div>
    </nav>
  )
}
